import { useState, useEffect, useRef, useMemo, type KeyboardEvent as ReactKeyboardEvent } from 'react'
import { ownerRouteToPath, type OwnerRoute } from '../ownerRouter'
import { useNls } from '../../../nls/nlsProvider'
import './ownerKit.css'
import { useRoles } from '../context/RolesContext'
import {
  getStandardOwnerCommands,
  type CommandCategory,
  type PaletteCommand,
} from './ownerStandardCommands'

export type { CommandCategory, PaletteCommand }

const STORAGE_KEY_PALETTE_RECENTS = 'timelining_owner_palette_recents'
const MAX_RECENTS = 5

const getStoredRecents = (): string[] => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return []
    const raw = window.localStorage.getItem(STORAGE_KEY_PALETTE_RECENTS)
    if (!raw) return []
    return JSON.parse(raw) as string[]
  } catch {
    return []
  }
}

const storeRecent = (commandId: string): void => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return
    const current = getStoredRecents().filter((id) => id !== commandId)
    current.unshift(commandId)
    window.localStorage.setItem(
      STORAGE_KEY_PALETTE_RECENTS,
      JSON.stringify(current.slice(0, MAX_RECENTS)),
    )
  } catch {
    // Ignore private browsing exceptions
  }
}

export type OwnerCommandPaletteProps = {
  isOpen: boolean
  onClose: () => void
  commands?: PaletteCommand[]
  onNavigate?: (route: OwnerRoute) => void
}

export function OwnerCommandPalette({
  isOpen,
  onClose,
  commands,
  onNavigate,
}: OwnerCommandPaletteProps) {
  const { t } = useNls()
  const { role } = useRoles()
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLElement | null>(null)

  const allCommands = useMemo(() => {
    return commands ?? getStandardOwnerCommands(onNavigate, role)
  }, [commands, onNavigate, role])

  const [recentIds, setRecentIds] = useState<string[]>(getStoredRecents)

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (isOpen) {
      setQuery('')
      setSelectedIndex(0)
      setRecentIds(getStoredRecents())
    }
  }

  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement | null
      const timer = setTimeout(() => inputRef.current?.focus(), 30)
      return () => clearTimeout(timer)
    } else if (triggerRef.current) {
      triggerRef.current.focus()
    }
  }, [isOpen])

  const filteredCommands = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) {
      // Show recents first, then general commands
      const recentCmds = recentIds
        .map((id) => allCommands.find((c) => c.id === id))
        .filter((c): c is PaletteCommand => c !== undefined)
        .map((c) => ({ ...c, category: 'recent' as CommandCategory }))

      const nonRecent = allCommands.filter((c) => !recentIds.includes(c.id))
      return [...recentCmds, ...nonRecent]
    }

    return allCommands.filter((cmd) => {
      if (cmd.title.toLowerCase().includes(q)) return true
      if (cmd.hint?.toLowerCase().includes(q)) return true
      if (cmd.keywords?.some((k) => k.toLowerCase().includes(q))) return true
      return false
    })
  }, [allCommands, query, recentIds])

  const executeCommand = (cmd: PaletteCommand) => {
    storeRecent(cmd.id)
    onClose()
    cmd.perform()
  }

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((prev) => (filteredCommands.length ? (prev + 1) % filteredCommands.length : 0))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((prev) => (filteredCommands.length ? (prev - 1 + filteredCommands.length) % filteredCommands.length : 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredCommands[selectedIndex]) {
        executeCommand(filteredCommands[selectedIndex])
      }
    }
  }

  // Ensure selected item is scrolled into view
  useEffect(() => {
    const activeEl = listRef.current?.children[selectedIndex] as HTMLElement | undefined
    if (activeEl && listRef.current) {
      activeEl.scrollIntoView({ block: 'nearest' })
    }
  }, [selectedIndex])

  if (!isOpen) return null

  return (
    <div
      className="ownerCommandPaletteOverlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Command Palette"
      data-testid="owner-command-palette"
    >
      <div
        className="ownerCommandPaletteBox"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className="ownerCommandPaletteInputWrap">
          <input
            ref={inputRef}
            type="text"
            className="ownerCommandPaletteInput"
            placeholder={t('NLSOwnerConsoleCommandPalettePlaceholder')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIndex(0)
            }}
            autoComplete="off"
            spellCheck="false"
          />
        </div>

        <div className="ownerCommandPaletteList" ref={listRef} role="listbox">
          {filteredCommands.length === 0 ? (
            <div className="ownerCommandPaletteEmpty">
              {t('NLSOwnerConsoleNoResults')}
            </div>
          ) : (
            filteredCommands.map((cmd, idx) => (
              <div
                key={`${cmd.category}-${cmd.id}`}
                className={`ownerCommandItem ${idx === selectedIndex ? 'ownerCommandItem--selected' : ''}`}
                onClick={() => executeCommand(cmd)}
                role="option"
                aria-selected={idx === selectedIndex}
              >
                <div className="ownerCommandItemMain">
                  <span className="ownerCommandItemTitle">{cmd.title}</span>
                  {cmd.category === 'recent' && (
                    <span className="ownerCardMetaChip" style={{ fontSize: '10px' }}>
                      Recent
                    </span>
                  )}
                </div>
                {cmd.hint && <span className="ownerCommandItemHint">{cmd.hint}</span>}
              </div>
            ))
          )}
        </div>

        <div className="ownerCommandPaletteFooter">
          <span>{filteredCommands.length} results</span>
          <div className="ownerCommandPaletteFooterKeys">
            <span><strong>↑↓</strong> navigate</span>
            <span><strong>↵</strong> select</span>
            <span><strong>esc</strong> close</span>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ==========================================================================
   Keyboard Shortcuts Help Sheet Modal (?)
   ========================================================================== */

export type OwnerShortcutsSheetProps = {
  isOpen: boolean
  onClose: () => void
}

export function OwnerShortcutsSheet({ isOpen, onClose }: OwnerShortcutsSheetProps) {
  const { t } = useNls()

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const shortcuts = [
    { key: '⌘K / Ctrl+K', desc: 'Open command palette' },
    { key: '[', desc: 'Collapse / expand sidebar rail' },
    { key: 'g h', desc: 'Go to Home' },
    { key: 'g w', desc: 'Go to Workspaces' },
    { key: 'g i', desc: 'Go to Support Inbox' },
    { key: 'j / k', desc: 'Next / previous ticket in list' },
    { key: 'o / Enter', desc: 'Open selected ticket' },
    { key: 'r', desc: 'Reply in ticket detail' },
    { key: 'n', desc: 'Internal note in ticket detail' },
    { key: 's', desc: 'Snooze menu (or bulk snooze)' },
    { key: 't', desc: 'Tag ticket (or bulk tag)' },
    { key: 'p', desc: 'Priority menu (or bulk priority)' },
    { key: '#', desc: 'Jump to ticket by ID' },
    { key: '⌘/ / Ctrl+/', desc: 'Insert macro in ticket compose' },
    { key: '/', desc: 'Focus in-view search (or canned reply)' },
    { key: '?', desc: 'Show this keyboard shortcuts sheet' },
    { key: 'Esc', desc: 'Close ticket detail / dialog / palette' },
  ]


  return (
    <div className="ownerModalOverlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="ownerModalDialog" onClick={(e) => e.stopPropagation()}>
        <div className="ownerModalHeader">
          <h3>{t('NLSOwnerConsoleShortcuts')}</h3>
        </div>
        <div className="ownerModalBody">
          <div className="ownerShortcutsGrid">
            {shortcuts.map((s, idx) => (
              <div key={idx} className="ownerShortcutRow">
                <span>{s.desc}</span>
                <span className="ownerShortcutKey">{s.key}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="ownerModalActions">
          <button type="button" className="ownerModalBtn ownerModalBtn--default" onClick={onClose}>
            {t('NLSOwnerConsoleCancel')}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ==========================================================================
   Header Recents Dropdown (Item 3 in Header contract)
   ========================================================================== */

const STORAGE_KEY_HEADER_RECENTS = 'timelining_owner_header_recents'

const getStoredHeaderRecents = (): Array<{ path: string; label: string }> => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return []
    const raw = window.localStorage.getItem(STORAGE_KEY_HEADER_RECENTS)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

export function OwnerRecentsDropdown({
  currentRoute,
  onNavigate,
}: {
  currentRoute: OwnerRoute
  onNavigate?: (route: OwnerRoute) => void
}) {
  const { t } = useNls()
  const [open, setOpen] = useState(false)
  const [recents, setRecents] = useState<Array<{ path: string; label: string }>>([])
  const containerRef = useRef<HTMLDivElement>(null)

  // Track route history in localStorage
  useEffect(() => {
    try {
      if (typeof window === 'undefined' || !window.localStorage) return
      const currentPath = ownerRouteToPath(currentRoute)
      const label = currentRoute.view === 'home' ? 'Home' : currentRoute.view
      const raw = window.localStorage.getItem(STORAGE_KEY_HEADER_RECENTS)
      let list: Array<{ path: string; label: string }> = raw ? JSON.parse(raw) : []
      list = list.filter((item) => item.path !== currentPath)
      list.unshift({ path: currentPath, label })
      list = list.slice(0, 6)
      window.localStorage.setItem(STORAGE_KEY_HEADER_RECENTS, JSON.stringify(list))
    } catch {
      // Ignore
    }
  }, [currentRoute])

  const handleToggle = () => {
    const next = !open
    if (next) {
      setRecents(getStoredHeaderRecents())
    }
    setOpen(next)
  }

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  return (
    <div className="ownerRecentsDropdownContainer" ref={containerRef}>
      <button
        type="button"
        className="ownerRecentsBtn"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>{t('NLSOwnerConsoleRecents')}</span>
        <span>▾</span>
      </button>

      {open && (
        <div className="ownerRecentsMenu" role="menu">
          {recents.length === 0 ? (
            <div className="ownerRecentsMenuEmpty">No recent visits</div>
          ) : (
            recents.map((item, idx) => (
              <button
                key={idx}
                type="button"
                className="ownerRecentsMenuItem"
                role="menuitem"
                onClick={() => {
                  setOpen(false)
                  if (onNavigate) {
                    // Navigate via history push
                    window.history.pushState(null, '', item.path)
                    window.dispatchEvent(new PopStateEvent('popstate'))
                  } else {
                    window.location.href = item.path
                  }
                }}
              >
                <span>{item.label}</span>
                <span style={{ fontSize: '11px', color: 'var(--text-low)' }}>{item.path.replace('/sole-con-mana-ment-ge', '') || '/'}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
